// PeriodicEntries.js — CRUD UI for pharmace_common_db.periodic_entries
//
// Schema (confirmed):
//   { pharmacyId, schedule: weekly|monthly|annual,
//     period: { year, month?, isoWeek? },
//     entryScope, data: { ... open-shape ... },
//     enteredBy, enteredAt, updatedAt }
//
// The `data` shape is open — we render it as a dynamic key/value table so
// any data dictionary item (items_claimed, MURs_done, etc) can be entered
// without UI changes.

import React, { useState, useEffect, useCallback } from "react";

const API_BASE = "https://api.pharmacegroup.co.uk";

async function apiReq(method, path, body, token) {
  const opts = { method, headers: { Authorization: `Bearer ${token}` } };
  if (body !== undefined) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE}/api/headoffice/${path}`, opts);
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`${path} → HTTP ${res.status} ${t}`);
  }
  return res.json();
}

const S = {
  wrap:     { padding: 16, fontFamily: "system-ui, sans-serif" },
  h1:       { fontSize: 22, fontWeight: 600, margin: "0 0 6px" },
  sub:      { fontSize: 13, color: "#6b7280", margin: "0 0 16px" },
  panel:    { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6,
              padding: 14, marginBottom: 16 },
  panelTitle:{ fontSize: 15, fontWeight: 600, marginBottom: 8 },
  filterRow:{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  table:    { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:       { textAlign: "left", borderBottom: "1px solid #d1d5db",
              padding: "8px", fontWeight: 600, color: "#374151",
              background: "#f9fafb" },
  td:       { borderBottom: "1px solid #f3f4f6", padding: "8px",
              color: "#1f2937", verticalAlign: "top" },
  btn:      { background: "#2563eb", color: "#fff", border: "none",
              borderRadius: 4, padding: "5px 10px", cursor: "pointer",
              fontSize: 12 },
  btnDanger:{ background: "#dc2626", color: "#fff", border: "none",
              borderRadius: 4, padding: "5px 10px", cursor: "pointer",
              fontSize: 12 },
  btnGhost: { background: "#f3f4f6", color: "#374151",
              border: "1px solid #d1d5db", borderRadius: 4,
              padding: "5px 10px", cursor: "pointer", fontSize: 12 },
  btnPrim:  { background: "#059669", color: "#fff", border: "none",
              borderRadius: 4, padding: "8px 14px", cursor: "pointer",
              fontSize: 13, fontWeight: 600 },
  input:    { padding: "5px 8px", border: "1px solid #d1d5db",
              borderRadius: 4, fontSize: 13 },
  label:    { fontSize: 12, color: "#374151", marginRight: 6 },
  toast:    { position: "fixed", bottom: 20, right: 20,
              background: "#1f2937", color: "#fff", padding: "10px 14px",
              borderRadius: 4, fontSize: 13, zIndex: 1000 },
  error:    { color: "#b91c1c", fontSize: 13, padding: "4px 0" },
  formGrid: { display: "grid", gridTemplateColumns: "120px 1fr",
              gap: 10, alignItems: "center", marginBottom: 12 },
  modalBg:  { position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              background: "rgba(0,0,0,0.4)", display: "flex",
              justifyContent: "center", alignItems: "flex-start",
              zIndex: 100, paddingTop: 60 },
  modal:    { background: "#fff", borderRadius: 6, padding: 20,
              width: 540, maxHeight: "85vh", overflowY: "auto",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }
};

const SCHEDULES = ["weekly", "monthly", "annual"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// ---------------------------------------------------------------------------
// Data editor — dynamic key/value
// ---------------------------------------------------------------------------
function DataEditor({ data, setData }) {
  const entries = Object.entries(data || {});
  const updateRow = (i, newKey, newVal) => {
    const next = entries.map(([k, v], idx) => idx === i ? [newKey, newVal] : [k, v]);
    setData(Object.fromEntries(next.filter(([k]) => k !== "")));
  };
  const removeRow = (i) => {
    setData(Object.fromEntries(entries.filter((_, idx) => idx !== i)));
  };
  const addRow = () => {
    const next = { ...data };
    let i = 1;
    while (next[`new_field_${i}`] !== undefined) i++;
    next[`new_field_${i}`] = "";
    setData(next);
  };

  return (
    <div>
      <table style={{ ...S.table, marginBottom: 6 }}>
        <thead>
          <tr><th style={S.th}>Field</th><th style={S.th}>Value</th><th style={S.th}></th></tr>
        </thead>
        <tbody>
          {entries.length === 0 && (
            <tr><td style={S.td} colSpan={3}>No fields yet.</td></tr>
          )}
          {entries.map(([k, v], i) => (
            <tr key={i}>
              <td style={S.td}>
                <input
                  style={{ ...S.input, width: "100%" }}
                  value={k}
                  onChange={e => updateRow(i, e.target.value, v)}
                />
              </td>
              <td style={S.td}>
                <input
                  style={{ ...S.input, width: "100%" }}
                  value={v}
                  onChange={e => {
                    // Try to keep numbers as numbers
                    const raw = e.target.value;
                    const num = Number(raw);
                    updateRow(i, k, raw !== "" && !isNaN(num) ? num : raw);
                  }}
                />
              </td>
              <td style={S.td}>
                <button style={S.btnGhost} onClick={() => removeRow(i)}>×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button style={S.btnGhost} onClick={addRow}>+ Add field</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entry form (create / edit)
// ---------------------------------------------------------------------------
function EntryForm({ initial, pharmacies, onSave, onCancel }) {
  const now = new Date();
  const [form, setForm] = useState(() => initial ? {
    pharmacyId: String(initial.pharmacyId),
    schedule: initial.schedule,
    year: initial.period?.year || now.getFullYear(),
    month: initial.period?.month || null,
    isoWeek: initial.period?.isoWeek || null,
    entryScope: initial.entryScope || "",
    data: initial.data || {}
  } : {
    pharmacyId: pharmacies[0] ? String(pharmacies[0].pharmacyId) : "",
    schedule: "monthly",
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    isoWeek: null,
    entryScope: "",
    data: {}
  });

  const setData = (data) => setForm({ ...form, data });

  const submit = () => {
    const period = { year: Number(form.year) };
    if (form.schedule === "monthly") period.month = Number(form.month);
    if (form.schedule === "weekly")  period.isoWeek = Number(form.isoWeek);
    onSave({
      pharmacyId: form.pharmacyId,
      schedule: form.schedule,
      period,
      entryScope: form.entryScope || null,
      data: form.data
    });
  };

  return (
    <div style={S.modalBg} onClick={onCancel}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={{ ...S.panelTitle, marginBottom: 14 }}>
          {initial ? "Edit entry" : "New entry"}
        </div>

        <div style={S.formGrid}>
          <div style={S.label}>Pharmacy</div>
          <select
            style={S.input}
            value={form.pharmacyId}
            onChange={e => setForm({ ...form, pharmacyId: e.target.value })}
            disabled={!!initial}
          >
            {pharmacies.map(p => (
              <option key={String(p.pharmacyId)} value={String(p.pharmacyId)}>
                {p.pharmacyName}
              </option>
            ))}
          </select>

          <div style={S.label}>Schedule</div>
          <select
            style={S.input}
            value={form.schedule}
            onChange={e => setForm({ ...form, schedule: e.target.value })}
          >
            {SCHEDULES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <div style={S.label}>Year</div>
          <input
            type="number" style={S.input} value={form.year}
            onChange={e => setForm({ ...form, year: e.target.value })}
          />

          {form.schedule === "monthly" && (
            <>
              <div style={S.label}>Month</div>
              <select
                style={S.input}
                value={form.month || 1}
                onChange={e => setForm({ ...form, month: Number(e.target.value) })}
              >
                {MONTHS.map((m, i) => (
                  <option key={i} value={i + 1}>{i + 1} — {m}</option>
                ))}
              </select>
            </>
          )}

          {form.schedule === "weekly" && (
            <>
              <div style={S.label}>ISO week</div>
              <input
                type="number" style={S.input} min={1} max={53}
                value={form.isoWeek || ""}
                onChange={e => setForm({ ...form, isoWeek: Number(e.target.value) })}
              />
            </>
          )}

          <div style={S.label}>Scope label</div>
          <input
            style={S.input} placeholder="e.g. April 2026"
            value={form.entryScope}
            onChange={e => setForm({ ...form, entryScope: e.target.value })}
          />
        </div>

        <div style={S.panelTitle}>Data</div>
        <DataEditor data={form.data} setData={setData} />

        <div style={{ display: "flex", gap: 10, marginTop: 16,
                      justifyContent: "flex-end" }}>
          <button style={S.btnGhost} onClick={onCancel}>Cancel</button>
          <button style={S.btnPrim} onClick={submit}>
            {initial ? "Save changes" : "Create entry"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main PeriodicEntries component
// ---------------------------------------------------------------------------
export default function PeriodicEntries({ token: tokenProp } = {}) {
  const [token] = useState(() =>
    tokenProp ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    ""
  );
  const [pharmacies, setPharmacies] = useState([]);
  const [filter, setFilter] = useState({
    pharmacyId: "", schedule: "", year: new Date().getFullYear()
  });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  // Pull pharmacy list from fridge.active-pharmacies as a cheap source.
  // (Any module's active-pharmacies endpoint returns the same shape.)
  useEffect(() => {
    if (!token) return;
    apiReq("GET", "fridge/active-pharmacies", undefined, token)
      .then(setPharmacies)
      .catch(e => setError(e.message));
  }, [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.pharmacyId) params.set("pharmacyId", filter.pharmacyId);
      if (filter.schedule)   params.set("schedule", filter.schedule);
      if (filter.year)       params.set("year", filter.year);
      const r = await apiReq("GET",
        `periodic-entries?${params.toString()}`, undefined, token);
      setEntries(r.entries);
      setError(null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async (payload) => {
    try {
      if (editing) {
        await apiReq("PUT", `periodic-entries/${editing._id}`, payload, token);
        showToast("Entry updated");
      } else {
        await apiReq("POST", "periodic-entries", payload, token);
        showToast("Entry created");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) { showToast(e.message); }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await apiReq("DELETE", `periodic-entries/${entry._id}`, undefined, token);
      showToast("Entry deleted");
      load();
    } catch (e) { showToast(e.message); }
  };

  const formatPeriod = (e) => {
    if (e.schedule === "weekly") return `${e.period.year} wk ${e.period.isoWeek}`;
    if (e.schedule === "monthly") {
      const m = MONTHS[e.period.month - 1] || `m${e.period.month}`;
      return `${m} ${e.period.year}`;
    }
    if (e.schedule === "annual") return `${e.period.year}`;
    return JSON.stringify(e.period);
  };

  if (!token) {
    return <div style={S.wrap}><div style={S.error}>Not signed in.</div></div>;
  }

  return (
    <div style={S.wrap}>
      <h1 style={S.h1}>Periodic Entries</h1>
      <div style={S.sub}>
        Manual data entry for weekly / monthly / annual figures
        (e.g. items dispensed, MURs done, services delivered).
      </div>

      <div style={S.panel}>
        <div style={S.filterRow}>
          <div>
            <span style={S.label}>Pharmacy</span>
            <select
              style={S.input}
              value={filter.pharmacyId}
              onChange={e => setFilter({ ...filter, pharmacyId: e.target.value })}
            >
              <option value="">All</option>
              {pharmacies.map(p => (
                <option key={String(p.pharmacyId)} value={String(p.pharmacyId)}>
                  {p.pharmacyName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span style={S.label}>Schedule</span>
            <select
              style={S.input}
              value={filter.schedule}
              onChange={e => setFilter({ ...filter, schedule: e.target.value })}
            >
              <option value="">All</option>
              {SCHEDULES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <span style={S.label}>Year</span>
            <input
              type="number" style={{ ...S.input, width: 90 }}
              value={filter.year}
              onChange={e => setFilter({ ...filter, year: Number(e.target.value) })}
            />
          </div>
          <button style={S.btnPrim} onClick={() => setCreating(true)}>
            + New entry
          </button>
        </div>
      </div>

      {error && <div style={S.error}>{error}</div>}
      {loading && <div>Loading…</div>}

      <div style={S.panel}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Pharmacy</th>
              <th style={S.th}>Schedule</th>
              <th style={S.th}>Period</th>
              <th style={S.th}>Scope</th>
              <th style={S.th}>Data</th>
              <th style={S.th}>Last updated</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && !loading && (
              <tr><td style={S.td} colSpan={7}>No entries match the filter.</td></tr>
            )}
            {entries.map(e => {
              const pharmacy = pharmacies.find(
                p => String(p.pharmacyId) === String(e.pharmacyId)
              );
              return (
                <tr key={e._id}>
                  <td style={S.td}>{pharmacy?.pharmacyName || String(e.pharmacyId)}</td>
                  <td style={S.td}>{e.schedule}</td>
                  <td style={S.td}>{formatPeriod(e)}</td>
                  <td style={S.td}>{e.entryScope || <span style={{color:"#9ca3af"}}>—</span>}</td>
                  <td style={S.td}>
                    {Object.entries(e.data || {}).slice(0, 4).map(([k, v]) => (
                      <div key={k} style={{ fontSize: 12 }}>
                        <code>{k}</code>: <b>{String(v)}</b>
                      </div>
                    ))}
                    {Object.keys(e.data || {}).length > 4 && (
                      <div style={{ fontSize: 11, color: "#6b7280" }}>
                        … and {Object.keys(e.data).length - 4} more
                      </div>
                    )}
                  </td>
                  <td style={S.td}>
                    {e.updatedAt && new Date(e.updatedAt).toLocaleDateString()}
                  </td>
                  <td style={S.td}>
                    <button style={S.btn} onClick={() => setEditing(e)}>Edit</button>{" "}
                    <button style={S.btnDanger} onClick={() => handleDelete(e)}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <EntryForm
          initial={editing}
          pharmacies={pharmacies}
          onSave={handleSave}
          onCancel={() => { setCreating(false); setEditing(null); }}
        />
      )}

      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}
